import { useBlockExplorerAddressLink } from '@/domain/hooks/useBlockExplorerAddressLink'
import type { CheckedAddress } from '@/domain/types/CheckedAddress'
import type { Token } from '@/domain/types/Token'
import { Address } from '@/ui/atoms/address/Address'
import { Link } from '@/ui/atoms/link/Link'
import { TokenIcon } from '@/ui/atoms/token-icon/TokenIcon'
import { Typography } from '@/ui/atoms/typography/Typography'
import { ExternalLink } from 'lucide-react'

export interface AddressItem {
  label: string
  address: CheckedAddress
  token: Token
}

function AddressLink({ item, chainId }: { item: AddressItem; chainId: number }) {
  const href = useBlockExplorerAddressLink({ address: item.address, chainId })
  const content = (
    <div className="flex flex-col gap-1">
      <span className="text-muted-foreground text-xs tracking-[-0.3px]">{item.label}</span>
      <span className="flex items-center gap-2 font-medium text-base text-white transition-colors group-hover:text-primary">
        <TokenIcon token={item.token} className="h-4 w-4" />
        <Address
          address={item.address}
          compact
          postfix={href ? <ExternalLink className="h-3.5 w-3.5" /> : undefined}
        />
      </span>
    </div>
  )

  if (!href) return content

  return (
    <Link href={href} external className="group text-foreground hover:text-foreground">
      {content}
    </Link>
  )
}

interface AddressesPanelProps {
  addresses: AddressItem[]
  chainId: number
}

export function AddressesPanel({ addresses, chainId }: AddressesPanelProps) {
  return (
    <div className="flex flex-col items-start gap-6 rounded-2xl border bg-panel p-6 shadow-2xs">
      <div className="w-full">
        <Typography variant="h3" className="mb-6 text-xl">
          Addresses
        </Typography>

        <div className="flex flex-col items-start gap-6 md:grid md:grid-cols-3 md:gap-x-6 md:gap-y-6">
          {addresses.map((item) => (
            <AddressLink key={item.address} item={item} chainId={chainId} />
          ))}
        </div>
      </div>
    </div>
  )
}
